import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home, MapPin, Mountain, Layers } from 'lucide-react';
import { journeyTracker } from '../lib/journeyTracker';
import { masterRegistryEntries } from '../data/masterRegistryEntries';
import { isPrioritiesAiProductDesignPath } from '../lib/prioritiesAiProductDesign';

const PRIORITIES_AI_PRODUCT_SUBLABELS: Record<string, string> = {
  colors: 'Colors',
  typography: 'Typography',
  'icon-library': 'Icon library',
  'brand-elements': 'Brand elements',
  'component-library': 'Component library',
  'visual-design-grammar': 'Visual grammar',
  'patterns-background-elements': 'Patterns & backgrounds',
  'infographic-data-viz-standards': 'Data visualization',
  'layout-grid-system': 'Layout & grid',
  'tone-voice-system': 'Tone & voice',
  'best-practices-design-principles': 'Principles',
};

interface BreadcrumbItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  context?: string;
}

export const ContextualBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [recentContext, setRecentContext] = useState<string[]>([]);

  useEffect(() => {
    const crumbs = buildBreadcrumbs(location.pathname);
    setBreadcrumbs(crumbs);

    // Get recent journey context
    const journey = journeyTracker.getJourney();
    const recentPages = (journey.recentPages || [])
      .slice(0, 3)
      .filter(p => p.path !== location.pathname)
      .map(p => p.path);
    setRecentContext(recentPages);
  }, [location]);

  if (isPrioritiesAiProductDesignPath(location.pathname)) return null;

  if (breadcrumbs.length <= 1) return null;

  return (
    <div className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-4 mb-6">
      {/* Main Breadcrumbs */}
      <nav className="flex items-center flex-wrap gap-2 text-sm">
        {breadcrumbs.map((crumb, idx) => {
          const isLast = idx === breadcrumbs.length - 1;
          
          return (
            <React.Fragment key={idx}>
              {isLast ? (
                <div className="flex items-center gap-2 text-charcoal-900 font-semibold">
                  {crumb.icon}
                  <span>{crumb.label}</span>
                  {crumb.context && (
                    <span className="text-xs text-charcoal-500 font-normal ml-1">
                      ({crumb.context})
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to={crumb.path}
                    className="flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                  >
                    {crumb.icon}
                    <span>{crumb.label}</span>
                  </Link>
                  <ChevronRight className="h-4 w-4 text-charcoal-400" />
                </>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Recent Context */}
      {recentContext.length > 0 && (
        <div className="mt-3 pt-3 border-t border-charcoal-200">
          <div className="flex items-center gap-2 text-xs text-charcoal-600">
            <span className="font-semibold">Recently visited:</span>
            <div className="flex items-center gap-2 flex-wrap">
              {recentContext.map((path, idx) => {
                const label = getPathLabel(path);
                return (
                  <React.Fragment key={idx}>
                    <Link
                      to={path}
                      className="text-sage-600 hover:text-sage-800 hover:underline transition-colors"
                    >
                      {label}
                    </Link>
                    {idx < recentContext.length - 1 && (
                      <span className="text-charcoal-400">•</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function buildBreadcrumbs(rawPathname: string): BreadcrumbItem[] {
  // Label maps below carry flat-era keys; journey/breadcrumb logic is era-blind.
  const pathname = rawPathname.replace(/^\/elevation(?=\/|$)/, '') || '/';
  const crumbs: BreadcrumbItem[] = [
    {
      label: 'Home',
      path: '/',
      icon: <Home className="h-4 w-4" />,
    }
  ];

  // Parse path segments
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return [crumbs[0]];
  }

  // Handle different route patterns
  if (segments[0] === 'overview') {
    crumbs.push({
      label: 'MOF Architecture',
      path: '/overview',
      icon: <Layers className="h-4 w-4" />,
      context: 'System Overview',
    });
  }
  else if (segments[0] === 'engines') {
    crumbs.push({
      label: 'Engines',
      path: '/elevation/engines',
      icon: <Layers className="h-4 w-4" />,
      context: 'Infrastructure Layer',
    });
  }
  else if (segments[0] === 'grammar') {
    crumbs.push({
      label: 'M3: Metadata',
      path: '/elevation/grammar',
      icon: <Layers className="h-4 w-4" />,
      context: 'Meta-Stack',
    });
  }
  else if (segments[0] === 'models') {
    crumbs.push({
      label: 'M2: Models',
      path: '/elevation/models',
      icon: <Layers className="h-4 w-4" />,
      context: 'Frameworks & Models',
    });
  }
  else if (segments[0] === 'templates') {
    crumbs.push({
      label: 'M1: Templates',
      path: '/elevation/templates',
      icon: <Layers className="h-4 w-4" />,
      context: 'Reusable Patterns',
    });
  }
  else if (segments[0] === 'runtime') {
    crumbs.push({
      label: 'M0: Runtime',
      path: '/elevation/runtime',
      icon: <Layers className="h-4 w-4" />,
      context: 'Live Instances',
    });
  }
  else if (segments[0] === 'island') {
    crumbs.push({
      label: 'Island Explorer',
      path: '/elevation/island',
      icon: <MapPin className="h-4 w-4" />,
      context: 'Domain Exploration',
    });
  }
  else if (segments[0] === 'mountain') {
    crumbs.push({
      label: 'Mountain Ascent',
      path: '/elevation/mountain',
      icon: <Mountain className="h-4 w-4" />,
      context: 'Capability Development',
    });
  }
  else if (segments[0] === 'glossary') {
    crumbs.push({
      label: 'Glossary',
      path: '/elevation/glossary',
      context: 'Terms & Definitions',
    });
  }
  else if (segments[0] === 'advanced-search') {
    crumbs.push({
      label: 'Advanced Search',
      path: '/elevation/advanced-search',
      context: 'Find & Filter',
    });
  }
  else if (segments[0] === 'design-language') {
    crumbs.push({
      label: 'Design Language',
      path: '/elevation/design-language',
      context: 'Verbal & Visual',
    });
  }
  else if (segments[0] === 'priorities-ai-design-language') {
    crumbs.push({
      label: 'Product Design',
      path: '/elevation/priorities-ai-design-language',
      context: 'Priorities.ai',
    });
    if (segments.length > 1 && segments[1]) {
      const sub = PRIORITIES_AI_PRODUCT_SUBLABELS[segments[1]];
      crumbs.push({
        label: typeof sub === 'string' ? sub : segments[1].replace(/-/g, ' '),
        path: `/elevation/priorities-ai-design-language/${segments[1]}`,
      });
    }
  }

  else if (segments[0] === 'progress') {
    crumbs.push({
      label: 'Your Progress',
      path: '/elevation/progress',
      context: 'Learning Dashboard',
    });
  }

  // Handle entry detail pages
  if (segments[0] === 'entry' && segments[1]) {
    // Add parent (Models)
    crumbs.push({
      label: 'Models',
      path: '/elevation/models',
      icon: <Layers className="h-4 w-4" />,
    });

    // Add specific entry
    const entry = masterRegistryEntries.find(e => e.encodedId === segments[1]);
    if (entry) {
      crumbs.push({
        label: entry.canonicalName,
        path: `/elevation/entry/${entry.encodedId}`,
        context: entry.sectionCode.toUpperCase(),
      });
    }
  }

  return crumbs;
}

function getPathLabel(rawPath: string): string {
  const path = rawPath.replace(/^\/elevation(?=\/|$)/, '') || '/';
  const labels: Record<string, string> = {
    '/': 'Home',
    '/overview': 'Architecture',
    '/engines': 'Engines',
    '/grammar': 'Grammar',
    '/models': 'Models',
    '/templates': 'Templates',
    '/runtime': 'Runtime',
    '/island': 'Island',
    '/mountain': 'Mountain',
    '/glossary': 'Glossary',
    '/advanced-search': 'Search',
    '/design-language': 'Design',
    '/priorities-ai-design-language': 'Product design',
    '/progress': 'Progress',
    '/about': 'About',
  };

  // Handle entry paths
  if (path.startsWith('/entry/')) {
    const id = path.split('/')[2];
    const entry = masterRegistryEntries.find(e => e.encodedId === id);
    return entry ? entry.canonicalName : 'Entry';
  }

  if (path.startsWith('/priorities-ai-design-language/') && path !== '/priorities-ai-design-language') {
    const child = path.split('/')[2];
    return PRIORITIES_AI_PRODUCT_SUBLABELS[child] ?? 'Product design';
  }

  return labels[path] || path;
}

